import { Link } from "react-router-dom";
import { Phone, ArrowRight, Stethoscope, Accessibility, Hospital, Users2, Car } from "lucide-react";
import { T } from "@/lib/testIds";
import { PHONE } from "@/components/Layout";
import { SafetyShield, WingedClock, TrustHandshake } from "@/components/BrandIcons";

const SERVICES = [
  { key: "medical", title: "Medical Appointments", desc: "Reliable transportation to and from medical appointments.", Icon: Stethoscope, img: "/medappt.jpg" },
  { key: "wheelchair", title: "Wheelchair Transportation", desc: "Safe and comfortable transportation for passengers requiring wheelchair accessibility.", Icon: Accessibility, img: "https://images.pexels.com/photos/35831410/pexels-photo-35831410.jpeg" },
  { key: "hospital", title: "Hospital Transportation", desc: "Reliable transportation for hospital visits, appointments, and discharge.", Icon: Hospital, img: "https://images.unsplash.com/photo-1764885517847-79d62138cc58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzg3NjAzOTEzfDA&ixlib=rb-4.1.0&q=85" },
  { key: "senior", title: "Senior Transportation", desc: "Comfortable and respectful transportation for seniors and individuals requiring additional assistance.", Icon: Users2, img: "https://images.unsplash.com/photo-1773227055624-07b515ba87c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxzZW5pb3IlMjBwYXRpZW50JTIwY2FyZWdpdmVyfGVufDB8fHx8MTc4NzYwMzkwNXww&ixlib=rb-4.1.0&q=85" },
  { key: "private", title: "Private Transportation", desc: "Discreet, on-demand private rides for non-medical trips and errands.", Icon: Car, img: "https://images.pexels.com/photos/1592384/pexels-photo-1592384.jpeg" },
];

const VALUES = [
  { Icon: SafetyShield, title: "Safety First", desc: "Your safety and comfort come first on every ride." },
  { Icon: WingedClock, title: "On Time, Every Time", desc: "We value your time and appointments." },
  { Icon: TrustHandshake, title: "Service You Can Trust", desc: "Experienced, respectful, and compassionate professionals." },
];

export default function Home() {
  return (
    <div className="animate-fade-up">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid={T.home.hero}>
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-bl from-blue-50 via-white to-transparent" />
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-100/40 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A192F] leading-[1.05]">
              Compassionate care, <span className="text-emerald-600">every mile.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Non-emergency medical transportation designed around your needs — dignified, professional, and always on time.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${PHONE.replace(/\D/g, "")}`}
                data-testid={T.home.callUs}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-lg shadow-emerald-500/25 transition-colors"
              >
                <Phone className="w-4 h-4" /> Call Us <span className="opacity-80 font-normal hidden sm:inline">· {PHONE}</span>
              </a>
              <Link
                to="/contact"
                data-testid={T.home.contactUs}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white border-2 border-slate-200 hover:border-[#1D4ED8] hover:text-[#1D4ED8] text-slate-800 font-semibold transition-colors"
              >
                Contact Us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2"><SafetyShield size={22} /> Safety First</div>
              <div className="flex items-center gap-2"><WingedClock size={28} /> On Time, Every Time</div>
              <div className="hidden sm:flex items-center gap-2"><TrustHandshake size={28} /> Service You Can Trust</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-blue-100 to-emerald-100 -z-10 blur-2xl opacity-70" />
              <div className="rounded-3xl overflow-hidden shadow-2xl border border-slate-200/70 bg-white">
                <img
                  src="https://images.pexels.com/photos/31627609/pexels-photo-31627609.jpeg"
                  alt="Happy Moreno Quality Transportation driver ready to help"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 pr-6 flex items-center gap-3 max-w-[240px]">
                <div className="w-11 h-11 flex items-center justify-center">
                  <TrustHandshake size={38} />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Trusted, professional</div>
                  <div className="font-semibold text-[#0A192F] text-sm">Care every mile</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="home-services">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-2">Our Services</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A192F]">Transportation built around care</h2>
          </div>
          <Link to="/services" className="text-sm font-semibold text-[#1D4ED8] hover:underline inline-flex items-center gap-1">See all services <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.slice(0, 4).map(({ key, title, desc, Icon, img }) => (
            <div
              key={key}
              data-testid={T.home.serviceCard(key)}
              className="group rounded-2xl bg-white border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 transition-[transform,box-shadow,border-color] duration-300"
            >
              <div className="relative h-40 overflow-hidden bg-slate-100">
                <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute bottom-3 left-3 w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#1D4ED8]" />
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-[#0A192F] text-lg">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
                <Link to="/services" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D4ED8] hover:gap-2 transition-[gap]">
                  Learn more <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="bg-slate-50 border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-8">
          {VALUES.map(({ Icon, title, desc }) => (
            <div key={title} className="rounded-2xl bg-white border border-slate-200 p-8 hover:shadow-lg transition-shadow">
              <div className="h-20 flex items-center">
                <Icon size={78} />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-[#0A192F]">{title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-lg">
          <img src="https://images.unsplash.com/photo-1773227055624-07b515ba87c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxzZW5pb3IlMjBwYXRpZW50JTIwY2FyZWdpdmVyfGVufDB8fHx8MTc4NzYwMzkwNXww&ixlib=rb-4.1.0&q=85" alt="Caregiver assistance" className="w-full h-[380px] object-cover" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-3">About Us</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A192F]">Care you can count on</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Moreno Quality Transportation is dedicated to safe, reliable, and compassionate non-emergency medical transportation. We treat every passenger with dignity, respect, and quality service — from the moment we arrive until we deliver you safely to your destination.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-3 text-sm">
            {["Safety", "Reliability", "Compassion", "Professionalism", "Respect", "Quality"].map((v) => (
              <li key={v} className="flex items-center gap-2 text-slate-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {v}</li>
            ))}
          </ul>
          <Link to="/about" className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0A192F] hover:bg-[#12244a] text-white font-semibold transition-colors">
            Learn More About Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CAREERS + STATUS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-gradient-to-br from-[#0A192F] to-[#12244a] text-white p-10 relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-60 h-60 rounded-full bg-emerald-500/15 blur-3xl" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-semibold mb-3">We're Hiring</div>
            <h3 className="text-3xl font-bold">Drive With Us</h3>
            <p className="mt-3 text-slate-300 max-w-md">Join a team of professional drivers helping patients get to their appointments safely and comfortably.</p>
            <Link to="/apply" data-testid={T.home.startApp} className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold">
              Start Your Application <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 p-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-3">Application Status</div>
          <h3 className="text-3xl font-bold text-[#0A192F]">Check your status</h3>
          <p className="mt-3 text-slate-600 max-w-md">Already applied? Check the current status of your driver application anytime — no account needed.</p>
          <Link to="/status" data-testid={T.home.checkStatus} className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-slate-200 hover:border-[#1D4ED8] hover:text-[#1D4ED8] font-semibold text-slate-800 transition-colors">
            Check Status <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
